import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, SwitchCamera, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { beep, soundEnabled, setSoundEnabled } from "@/lib/scanFx";

const COOLDOWN_MS = 1800;

/**
 * Scanner kamera QR/barcode (HP/tablet Android & webcam).
 * Kamera tetap terbuka setelah scan berhasil (mode berturut-turut) — kode yang sama
 * tidak dikirim ulang selama jeda COOLDOWN_MS agar tidak dobel.
 */
export function CameraScanner({ onScan, accent = "emerald", testid = "camera-scanner" }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [facing, setFacing] = useState("environment");
  const [last, setLast] = useState("");
  const [sound, setSound] = useState(soundEnabled());
  const idRef = useRef(`cam-${Math.random().toString(36).slice(2, 8)}`);
  const qrRef = useRef(null);
  const lastRef = useRef({ text: "", at: 0 });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const qr = new Html5Qrcode(idRef.current, { verbose: false });
    qrRef.current = qr;
    setError("");
    qr.start(
      { facingMode: facing },
      { fps: 10, qrbox: (w, h) => { const s = Math.floor(Math.min(w, h) * 0.7); return { width: s, height: s }; }, aspectRatio: 1 },
      (text) => {
        const now = Date.now();
        if (lastRef.current.text === text && now - lastRef.current.at < COOLDOWN_MS) return;
        lastRef.current = { text, at: now };
        setLast(text);
        beep("ok");
        onScan(text.trim().toUpperCase());
      },
      () => {},
    ).catch((e) => {
      if (cancelled) return;
      const msg = String(e?.message || e);
      setError(/NotAllowed|Permission/i.test(msg) ? "Izin kamera ditolak. Aktifkan izin kamera di browser lalu coba lagi."
        : /NotFound|Requested device/i.test(msg) ? "Kamera tidak ditemukan pada perangkat ini."
        : `Kamera tidak dapat dibuka: ${msg}`);
      setOpen(false);
    });
    return () => {
      cancelled = true;
      const inst = qrRef.current;
      qrRef.current = null;
      if (!inst) return;
      Promise.resolve(inst.isScanning ? inst.stop() : null).catch(() => {}).finally(() => { try { inst.clear(); } catch (_) { /* noop */ } });
    };
  }, [open, facing, onScan]);

  const ring = accent === "amber" ? "border-amber-400/60" : "border-emerald-400/60";
  const btn = accent === "amber" ? "bg-amber-600 hover:bg-amber-500 text-[#0B1220]" : "bg-emerald-600 hover:bg-emerald-500";

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        {!open ? (
          <Button type="button" onClick={() => setOpen(true)} className={`${btn} font-semibold`} data-testid={`${testid}-open`}>
            <Camera className="mr-2 h-4 w-4" /> Buka Kamera / 打开相机
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" className="border-slate-700" onClick={() => setOpen(false)} data-testid={`${testid}-close`}>
              <CameraOff className="mr-2 h-4 w-4" /> Tutup Kamera
            </Button>
            <Button type="button" variant="outline" className="border-slate-700" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} data-testid={`${testid}-switch`}>
              <SwitchCamera className="mr-2 h-4 w-4" /> {facing === "environment" ? "Belakang" : "Depan"}
            </Button>
          </>
        )}
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-slate-100" title={sound ? "Matikan suara scan" : "Nyalakan suara scan"}
          onClick={() => { const n = !sound; setSound(n); setSoundEnabled(n); if (n) beep("ok"); }} data-testid={`${testid}-sound`}>
          {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </Button>
        {last && <span className="font-mono text-xs text-slate-400" data-testid={`${testid}-last`}>Terakhir: <span className="text-amber-300">{last}</span></span>}
      </div>
      {error && <p className="mt-2 text-xs text-rose-400" data-testid={`${testid}-error`}>{error}</p>}
      <div className={open ? `relative mt-3 overflow-hidden rounded-lg border-2 ${ring} bg-black` : "hidden"} data-testid={`${testid}-viewport`}>
        <div id={idRef.current} className="mx-auto w-full max-w-sm [&_video]:!w-full [&_video]:!h-auto [&_video]:object-cover" />
        {open && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 text-center text-[11px] text-slate-300">
            Arahkan kamera ke label QR marking · kamera tetap aktif untuk scan berikutnya
          </div>
        )}
      </div>
    </div>
  );
}
